import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCarriers } from '../hooks/useCarriers'
import { useRequests } from '../hooks/useRequests'
import { useNotification } from '../contexts/NotificationContext'
import { useAuth } from '../contexts/AuthContext'
import { parseApiError } from '../utils/errorUtils'
import { mapCarrierTypeToBackend } from '../utils/transformers'
import './Home.css'

// Threshold for surfacing "needs attention" via the inline banner above
// the composer. The page intentionally only shows ONE attention signal
// when there's something the user must act on — otherwise it stays quiet.

const HOUR_MS = 60 * 60 * 1000
const STALE_AFTER_MS = 24 * HOUR_MS

// Placeholder tokens stamped per-carrier by the Lambda email sender.
// Must match the constants in ai-core/src/tools/email_sender_tool.py
// (CARRIER_NAME_PLACEHOLDER / REQUEST_ID_PLACEHOLDER). Keeping these
// as named constants here avoids the magic-string-everywhere class of
// drift bug.
const CARRIER_NAME_PLACEHOLDER = '{{CARRIER_NAME}}'
const REQUEST_ID_PLACEHOLDER = '{{REQUEST_ID}}'

// Briefing-strip helpers — date, greeting, short display name from email.
function getGreeting(now = new Date()) {
  const h = now.getHours()
  if (h < 5) return 'Good night'
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  if (h < 22) return 'Good evening'
  return 'Good night'
}

function formatBriefingDate(now = new Date()) {
  return now.toLocaleDateString(undefined, {
    weekday: 'long', month: 'short', day: 'numeric',
  }).toUpperCase()
}

function deriveShortName(email) {
  if (!email) return ''
  const local = String(email).split('@')[0]
  if (!local) return ''
  const cleaned = local.replace(/[._\-+]+/g, ' ').trim()
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

// Categorise requests that need user action. We only use the count from
// this list (for the inline banner). The list itself lives on /rfqs —
// the dashboard is intentionally not the place to triage them.
function buildActionItems(requests, carriers, limit = 6) {
  if (!Array.isArray(requests)) return []
  const now = Date.now()
  const carrierById = new Map((carriers || []).map(c => [c.id, c]))
  const items = []

  for (const r of requests) {
    const carrierName = carrierById.get(r.carrier)?.name || 'Carrier'

    if (r.rawStatus === 'needs_clarification' && !r.clarificationReplySent) {
      items.push({
        kind: 'clarification', severity: 1, request: r, carrierName,
        kicker: 'Reply needed',
        body: `${carrierName} asked a question`,
        cta: 'Reply',
      })
      continue
    }

    const isQuoted = r.rawStatus === 'completed' || r.rawStatus === 'accepted'
    const isUnseen = !r.lastSeenStatus || r.lastSeenStatus !== r.rawStatus
    if (isQuoted && isUnseen) {
      items.push({
        kind: 'quote', severity: 2, request: r, carrierName,
        kicker: 'Quote in',
        body: r.quotedPriceFormatted
          ? `${carrierName} · ${r.quotedPriceFormatted}`
          : `${carrierName} replied with a quote`,
        cta: 'Review',
      })
      continue
    }

    if (r.rawStatus === 'pending' || r.rawStatus === 'in_progress') {
      const created = r.createdAt ? new Date(r.createdAt).getTime() : null
      if (created && now - created > STALE_AFTER_MS) {
        const hrs = Math.max(1, Math.floor((now - created) / HOUR_MS))
        items.push({
          kind: 'stale', severity: 3, request: r, carrierName,
          kicker: `Idle ${hrs}h`,
          body: `${carrierName} hasn't replied`,
          cta: 'View',
        })
      }
      continue
    }

    if (r.rawStatus === 'offline_followup') {
      items.push({
        kind: 'offline', severity: 4, request: r, carrierName,
        kicker: 'Moved offline',
        body: `${carrierName} contacting directly`,
        cta: 'View',
      })
    }
  }

  items.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity - b.severity
    const at = a.request.createdAt ? new Date(a.request.createdAt).getTime() : 0
    const bt = b.request.createdAt ? new Date(b.request.createdAt).getTime() : 0
    return bt - at
  })

  return items.slice(0, limit)
}

function Home() {
  const navigate = useNavigate()
  const [rfqText, setRfqText] = useState('')
  const { showSuccess, showError, showInfo } = useNotification()

  // Submission flow state machine: null → submitting → processing → review → validating → sending → success → error
  const [submissionPhase, setSubmissionPhase] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [trackingRequests, setTrackingRequests] = useState([])
  // trackingRequests: [{ id, carrierName, carrierId, carrierType, status, extraction, error }]
  const [originalRfqText, setOriginalRfqText] = useState('') // preserve original for retry
  // Validation skip flow: a "Skip validation" checkbox appears only after
  // the user has tried validating once. Lets them retry without the gate.
  const [validationAttempted, setValidationAttempted] = useState(false)
  const [skipValidationChecked, setSkipValidationChecked] = useState(false)

  // Carrier data from API
  const {
    carriers,
    isLoading: carriersLoading,
    error: carriersError,
    createCarrier,
    refetch: refetchCarriers
  } = useCarriers()

  // Request API hook - get requests for stats and recent quotes
  const {
    requests,
    isLoading: requestsLoading,
    createRequest,
    pollForAIUpdate,
    confirmSend,
    extractRFQ,
    validateRFQ,
    getValidationStatus,
    submitRFQ,
  } = useRequests()

  const [selectedCarriers, setSelectedCarriers] = useState([])
  const [carrierSearch, setCarrierSearch] = useState('')
  const [isCarrierDropdownOpen, setIsCarrierDropdownOpen] = useState(false)
  const [showAddCarrierModal, setShowAddCarrierModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newCarrier, setNewCarrier] = useState({
    name: '',
    type: '',
    contactTypes: [],
    primaryContactType: '',
    contacts: {
      email: { address: '', name: '' },
      phone: { number: '', name: '' },
      website: { url: '', username: '', password: '' },
      api: { url: '', headers: '' }
    }
  })

  const dropdownRef = useRef(null)
  const editableRef = useRef(null)
  const [typingText, setTypingText] = useState('')
  const [typingFading, setTypingFading] = useState(false)

  // Typing animation for RFQ placeholder
  const typingExamples = [
    `Need quote for FCL shipment:\nOrigin: Shanghai, China\nDestination: Los Angeles, USA\nCargo: Electronics - 15 pallets\nWeight: 12,000 kg\nContainer: 40ft High Cube\nIncoterms: FOB\nReady date: April 5, 2026`,
    `We need to ship 8 tons of automotive parts from Stuttgart, Germany to Jebel Ali port in Dubai. Looking for the best ocean freight rate, preferably LCL since we only have about 6 CBM. CIF terms, need it there by end of April.`,
    `Air freight urgent:\nPickup: Tokyo Narita, Japan\nDeliver: JFK, New York, USA\nCargo: Medical imaging equipment (fragile)\n3 crates, 120x80x90 cm each\nTotal weight: 800 kg\nMust arrive within 5 business days\nRequires temperature control 15-25°C`,
    `Hi, can you quote me for moving 2x 20ft containers of textiles from Dhaka, Bangladesh to Felixstowe, UK? FOB terms. Cargo is ready mid-March. Nothing hazardous, standard dry containers are fine. Also need inland haulage to our warehouse in Birmingham.`,
    `Multimodal quote request:\nRoute: Shenzhen → Rotterdam → Warsaw\nOcean leg: Shenzhen to Rotterdam\nRail/truck leg: Rotterdam to Warsaw\nCargo: Consumer electronics, 22 pallets\nWeight: 14,500 kg\nVolume: 38 CBM\nContainer: 40ft standard\nTerms: DDP\nInsurance: Required`,
  ]

  useEffect(() => {
    if (rfqText) return

    let cancelled = false
    let timeoutId = null
    let intervalId = null
    let exampleIndex = 0

    const runTypingCycle = () => {
      if (cancelled) return
      setTypingFading(false)
      const example = typingExamples[exampleIndex % typingExamples.length]
      let charIndex = 0

      // Phase 1: Type forward
      intervalId = setInterval(() => {
        if (cancelled) { clearInterval(intervalId); return }
        charIndex++
        setTypingText(example.slice(0, charIndex))

        if (charIndex >= example.length) {
          clearInterval(intervalId)
          // Phase 2: Pause 3 seconds, then fade out
          timeoutId = setTimeout(() => {
            if (cancelled) return
            setTypingFading(true)
            // Wait for fade-out animation to complete, then clear and start next
            timeoutId = setTimeout(() => {
              if (cancelled) return
              setTypingText('')
              setTypingFading(false)
              exampleIndex++
              timeoutId = setTimeout(() => {
                if (!cancelled) runTypingCycle()
              }, 400)
            }, 600)
          }, 3000)
        }
      }, 35)
    }

    runTypingCycle()

    return () => {
      cancelled = true
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [rfqText])

  // Current user — drives the briefing-strip greeting.
  const { user } = useAuth()

  // Briefing values recomputed each render (cheap) so the greeting
  // rolls over at noon/5pm without a reload.
  const briefingNow = new Date()
  const greeting = getGreeting(briefingNow)
  const briefingDate = formatBriefingDate(briefingNow)
  const userShort = deriveShortName(user?.email)

  // Compute stats from real data
  const awaitingCount = (requests || []).filter(r =>
    r.status === 'Awaiting Response' || r.status === 'pending' || r.status === 'Processing'
  ).length

  const readyCount = (requests || []).filter(r =>
    r.status === 'Ready' || r.status === 'completed' || r.status === 'quoted'
  ).length

  const carrierCount = (carriers || []).length

  // Recent activity feed — 5 items pulls the user's eye toward
  // in-flight work, which is half of the JTBD. (Was 3.)
  const recentQuotes = (requests || []).slice(0, 5).map(r => ({
    id: r.id,
    origin: r.origin?.split(',')[0] || 'Unknown',
    destination: r.destination?.split(',')[0] || 'Unknown',
    status: r.status === 'Ready' ? 'Quoted' : r.status === 'Awaiting Response' ? 'Pending' : r.status,
    date: r.submittedDate,
    // "NEW" indicator: the rawStatus differs from the lastSeenStatus
    // recorded the last time the user opened this request's details.
    isUnseen: !r.lastSeenStatus || r.lastSeenStatus !== r.rawStatus,
  }))

  // The sorted, capped, severity-tagged action lane. Severity 1 means
  // the user is the bottleneck (carrier asked a question, waiting on them).
  const actionItems = buildActionItems(requests, carriers)
  const urgentCount = actionItems.filter(i => i.severity === 1).length

  const exampleRFQ = `Need quote for FCL shipment:
Origin: Shanghai, China
Destination: Los Angeles, USA
Cargo: Electronics - 15 pallets
Weight: 12,000 kg
Container: 40ft High Cube
Incoterms: FOB
Ready date: January 15, 2025
Special requirements: Temperature controlled`

  // Filter carriers based on search
  const filteredCarriers = (carriers || []).filter(carrier =>
    carrier.name?.toLowerCase().includes(carrierSearch.toLowerCase()) ||
    carrier.type?.toLowerCase().includes(carrierSearch.toLowerCase())
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCarrierDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleCarrierToggle = (carrier) => {
    const isSelected = selectedCarriers.some(c => c.id === carrier.id)
    if (isSelected) {
      // Remove carrier
      setSelectedCarriers(selectedCarriers.filter(c => c.id !== carrier.id))
    } else {
      // Add carrier
      setSelectedCarriers([...selectedCarriers, carrier])
    }
  }

  const handleRemoveCarrier = (carrierId) => {
    setSelectedCarriers(selectedCarriers.filter(c => c.id !== carrierId))
  }

  const handleToggleContactType = (type) => {
    const currentTypes = newCarrier.contactTypes || []
    let updatedTypes
    let updatedPrimary = newCarrier.primaryContactType

    if (currentTypes.includes(type)) {
      updatedTypes = currentTypes.filter(t => t !== type)
      if (updatedPrimary === type) {
        updatedPrimary = updatedTypes.length === 1 ? updatedTypes[0] : ''
      }
    } else {
      updatedTypes = [...currentTypes, type]
      if (updatedTypes.length === 1) {
        updatedPrimary = type
      }
    }

    setNewCarrier({
      ...newCarrier,
      contactTypes: updatedTypes,
      primaryContactType: updatedPrimary,
    })
  }

  const handleContactChange = (type, field, value) => {
    setNewCarrier({
      ...newCarrier,
      contacts: {
        ...newCarrier.contacts,
        [type]: {
          ...newCarrier.contacts[type],
          [field]: value
        }
      }
    })
  }

  const handleAddNewCarrier = async () => {
    const hasValidContact = newCarrier.contactTypes.some(type => {
      if (type === 'email') return newCarrier.contacts.email.address?.trim() && newCarrier.contacts.email.name?.trim()
      if (type === 'phone') return newCarrier.contacts.phone.number?.trim() && newCarrier.contacts.phone.name?.trim()
      if (type === 'website') return newCarrier.contacts.website.url?.trim()
      if (type === 'api') return newCarrier.contacts.api.url?.trim()
      return false
    })

    if (!newCarrier.name || !newCarrier.type || newCarrier.contactTypes.length === 0 || !hasValidContact || !newCarrier.primaryContactType) {
      showError('Please fill in all required fields and select a primary contact type')
      return
    }

    setIsSubmitting(true)
    try {
      const created = await createCarrier(newCarrier)
      showSuccess(`Carrier "${newCarrier.name}" added successfully!`)

      // Add to selected carriers if we got a response
      if (created) {
        setSelectedCarriers([...selectedCarriers, created])
      }

      setShowAddCarrierModal(false)
      setNewCarrier({
        name: '',
        type: '',
        contactTypes: [],
        primaryContactType: '',
        contacts: {
          email: { address: '', name: '' },
          phone: { number: '', name: '' },
          website: { url: '', username: '', password: '' },
          api: { url: '', headers: '' }
        }
      })
    } catch (err) {
      console.error('[Home] Add carrier failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!rfqText.trim() || selectedCarriers.length === 0) {
      showError('Please enter RFQ details and select at least one carrier')
      return
    }

    // Check for #no-validate flag — skip extraction, review, and validation entirely
    const skipAll = /#no-validate\b/i.test(rfqText)
    const cleanText = rfqText.replace(/#no-validate\b/gi, '').trim()

    setIsSubmitting(true)
    setTrackingRequests([])
    setOriginalRfqText(rfqText)

    if (skipAll) {
      // Bypass mode: skip extraction + review + validation → submit directly
      setSubmissionPhase('sending')
      try {
        const results = await Promise.allSettled(
          selectedCarriers.map(carrier =>
            submitRFQ(cleanText, carrier.id, {}, '')
          )
        )

        const failed = []
        results.forEach((result, i) => {
          if (result.status === 'rejected') {
            const apiErrors = result.reason?.response?.data?.errors
            const apiMessage = result.reason?.response?.data?.message
            failed.push(apiErrors?.length ? apiErrors.join('; ') : apiMessage || 'Send failed')
          }
        })

        if (failed.length === 0) {
          setSubmissionPhase('success')
          setTimeout(() => {
            setSubmissionPhase(null)
            setTrackingRequests([])
            setOriginalRfqText('')
            setRfqText('')
            setSelectedCarriers([])
            setCarrierSearch('')
            setValidationErrors([])
            setValidationAttempted(false)
            setSkipValidationChecked(false)
          }, 2500)
        } else {
          showError(failed.join('; '))
          setSubmissionPhase('error')
        }
      } catch (err) {
        console.error('[Home] Bypass submit failed:', err)
        const parsed = parseApiError(err)
        showError(parsed.message)
        setSubmissionPhase('error')
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    // Normal flow: extract → review → validate → submit
    setSubmissionPhase('submitting')
    try {
      const tracking = selectedCarriers.map(carrier => ({
        id: null,
        carrierName: carrier.name || 'Unknown',
        carrierId: carrier.id,
        carrierType: carrier.type || '',
        status: 'processing',
        extraction: null,
        error: null,
      }))
      setTrackingRequests(tracking)
      setSubmissionPhase('processing')

      // Pass the full set of unique freight modes the user is targeting.
      // Display strings ("Ocean Freight" / "Air Freight") must be mapped
      // back to backend codes ("ship" / "air") because the Lambda's
      // carrier-context map keys on backend codes.
      const carrierTypes = Array.from(new Set(
        selectedCarriers
          .map(c => mapCarrierTypeToBackend(c.type))
          .filter(Boolean)
      ))
      const extractionResult = await extractRFQ(cleanText, carrierTypes)

      if (extractionResult.status === 'failed') {
        const errorMsg = extractionResult.validation_errors?.join('; ') || extractionResult.message || 'Extraction failed'
        setTrackingRequests(tracking.map(t => ({ ...t, status: 'error', error: errorMsg })))
        showError(errorMsg)
        setSubmissionPhase('error')
        return
      }

      const updatedTracking = tracking.map(t => ({
        ...t,
        status: 'extracted',
        extraction: extractionResult.extraction,
      }))
      setTrackingRequests(updatedTracking)

      // Prefer the full carrier-bound email body (the exact text each
      // carrier will receive, with {{CARRIER_NAME}} / {{REQUEST_ID}}
      // placeholders that get stamped per-recipient at send time).
      // Fall back to the bare enriched_text if the Lambda version is
      // older and email_body isn't in the response.
      const reviewText = extractionResult.email_body || extractionResult.enriched_text
      if (!reviewText || !reviewText.trim()) {
        // Lambda returned an empty preview/body — don't strand the user
        // on a blank review screen. Surface a real error.
        console.error('[Home] Extraction returned empty preview text', extractionResult)
        showError('AI returned no preview text. Please try again or rephrase your RFQ.')
        setTrackingRequests(tracking.map(t => ({
          ...t, status: 'error', error: 'Empty preview from AI',
        })))
        setSubmissionPhase('error')
        return
      }
      setRfqText(reviewText.trim())

      setSubmissionPhase('review')
    } catch (err) {
      console.error('[Home] Extract RFQ failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
      setSubmissionPhase('error')
      setTrackingRequests([{ id: null, carrierName: '', carrierId: '', carrierType: '', status: 'error', extraction: null, error: parsed.message }])
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmSend = async () => {
    // Skip validation if the user ticked the checkbox (only visible after
    // the first attempted validation) OR if the hidden #no-validate keyword
    // is present in the text (kept as a power-user escape hatch).
    const rawText = editableRef.current ? editableRef.current.innerText : rfqText
    const skipValidation = skipValidationChecked || /#no-validate\b/i.test(rawText)
    let currentText = rawText.replace(/#no-validate\b/gi, '').trim()

    // Refuse to send an empty body. After the #no-validate strip the
    // text may collapse to whitespace — auto-restoring placeholders
    // around nothing would produce a content-free email.
    if (!currentText.trim()) {
      showError('Email body is empty. Please add your RFQ details before sending.')
      return
    }

    // Auto-restore deleted system placeholders. The user may edit the
    // email body freely but {{CARRIER_NAME}} (greeting) and {{REQUEST_ID}}
    // (reply-matching Ref) are required by the sender — if the user
    // deleted them, re-insert in their canonical positions. We don't
    // duplicate a greeting / Ref the user wrote themselves: if there's
    // already any "Dear / Hi / Hello / Hey / Greetings ..." line at
    // the top or a "Ref: RFQ-..." line anywhere, we leave it alone.
    const hasExistingGreeting = /^\s*(?:Dear|Hi|Hello|Hey|Greetings)\b/i.test(currentText)
    const hasExistingRef = /\bRef:\s*RFQ-/i.test(currentText)
    const placeholdersRestored = []
    if (!currentText.includes(CARRIER_NAME_PLACEHOLDER) && !hasExistingGreeting) {
      currentText = `Dear ${CARRIER_NAME_PLACEHOLDER},\n\n${currentText}`
      placeholdersRestored.push(CARRIER_NAME_PLACEHOLDER)
    }
    if (!currentText.includes(REQUEST_ID_PLACEHOLDER) && !hasExistingRef) {
      currentText = `${currentText.replace(/\s+$/, '')}\n\nRef: RFQ-${REQUEST_ID_PLACEHOLDER}`
      placeholdersRestored.push(REQUEST_ID_PLACEHOLDER)
    }
    if (placeholdersRestored.length > 0) {
      console.warn('[Home] Restored deleted placeholders:', placeholdersRestored)
      // Surface to the user so they understand their edit was
      // augmented — they can decide whether to cancel and rework.
      showInfo(`Restored required reference${placeholdersRestored.length > 1 ? 's' : ''} (${placeholdersRestored.join(', ')}). Carrier name and request ID are filled per carrier at send time.`)
    }

    // Check for unfilled placeholders [...]
    const missingMatch = currentText.match(/\[[^\]]+\]/g)
    if (missingMatch) {
      showError(`Please fill in the required fields: ${missingMatch.join(', ')}`)
      return
    }

    // Check for zero or negative values near cargo keywords
    const cargoKeywords = ['weight', 'volume', 'quantity', 'count', 'length', 'width', 'height', 'size', 'dimensions']
    const invalidFields = []
    const lowerText = currentText.toLowerCase()
    for (const kw of cargoKeywords) {
      let searchFrom = 0
      while (true) {
        const idx = lowerText.indexOf(kw, searchFrom)
        if (idx === -1) break
        const charBefore = idx > 0 ? lowerText[idx - 1] : ' '
        if (/[a-z0-9]/.test(charBefore)) {
          searchFrom = idx + 1
          continue
        }
        const after = lowerText.substring(idx + kw.length, idx + kw.length + 50)
        if (/(^|[\s:=])(-\d|0(?:\.0+)?(?!\d)(?!\.[1-9]))/.test(after)) {
          invalidFields.push(kw.charAt(0).toUpperCase() + kw.slice(1))
          break
        }
        searchFrom = idx + kw.length
      }
    }
    if (invalidFields.length > 0) {
      showError(`Zero or negative values not allowed for: ${invalidFields.join(', ')}. Please provide positive values.`)
      return
    }

    setValidationErrors([])
    setIsSubmitting(true)

    let validationId = ''

    if (skipValidation) {
      // Skip validation — go straight to sending
      setSubmissionPhase('sending')
    } else {
      // Step 1: Trigger async validation. Mark "attempted" so the
      // Skip-validation checkbox appears if we return to review.
      setSubmissionPhase('validating')
      setValidationAttempted(true)
      try {
        const { validation_id } = await validateRFQ(currentText)
        validationId = validation_id

        // Step 2: Poll for validation result every 5 seconds
        const POLL_INTERVAL = 5000
        const MAX_POLLS = 12 // 60 seconds max
        let validationPassed = false

        for (let i = 0; i < MAX_POLLS; i++) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL))
          const status = await getValidationStatus(validationId)

          if (status.status === 'passed') {
            validationPassed = true
            break
          }
          if (status.status === 'failed') {
            const errors = status.errors?.length ? status.errors : ['Validation failed']
            setValidationErrors(errors)
            setSubmissionPhase('review')
            setIsSubmitting(false)
            return
          }
        }

        if (!validationPassed) {
          setValidationErrors(['Validation timed out. Please try again.'])
          setSubmissionPhase('review')
          setIsSubmitting(false)
          return
        }
      } catch (err) {
        console.error('[Home] Validation error:', err)
        const parsed = parseApiError(err)
        setValidationErrors([parsed.message || 'Validation failed'])
        setSubmissionPhase('review')
        setIsSubmitting(false)
        return
      }

      setSubmissionPhase('sending')
    }

    try {
      // Submit to all carriers
      const extractedRequests = trackingRequests.filter(t => t.status === 'extracted' && t.extraction)
      const results = await Promise.allSettled(
        extractedRequests.map(t => submitRFQ(currentText, t.carrierId, t.extraction, validationId))
      )

      const succeeded = []
      const failed = []
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          succeeded.push({ ...extractedRequests[i], id: result.value?.id })
        } else {
          const apiErrors = result.reason?.response?.data?.errors
          const apiMessage = result.reason?.response?.data?.message
          const errorMsg = apiErrors?.length ? apiErrors.join('; ') : apiMessage || result.reason?.message || 'Send failed'
          failed.push({ ...extractedRequests[i], error: errorMsg })
        }
      })

      if (failed.length === 0) {
        // Show success in modal, then auto-reset form after delay
        setSubmissionPhase('success')
        setTimeout(() => {
          setSubmissionPhase(null)
          setTrackingRequests([])
          setOriginalRfqText('')
          setRfqText('')
          setSelectedCarriers([])
          setCarrierSearch('')
          setValidationErrors([])
          setValidationAttempted(false)
          setSkipValidationChecked(false)
        }, 2500)
      } else if (succeeded.length > 0) {
        setTrackingRequests(prev => prev.map(t => {
          if (succeeded.some(s => s.carrierId === t.carrierId)) return { ...t, status: 'sent' }
          const fail = failed.find(f => f.carrierId === t.carrierId)
          if (fail) return { ...t, status: 'error', error: fail.error }
          return t
        }))
        showError(`${failed.length} carrier(s) failed. ${succeeded.length} sent.`)
        setSubmissionPhase('review')
      } else {
        const allErrors = failed.map(f => f.error).filter(Boolean).join('; ')
        setValidationErrors(allErrors ? allErrors.split('; ') : ['Failed to send to all carriers'])
        setSubmissionPhase('review')
      }
    } catch (err) {
      console.error('[Home] Submit error:', err)
      const parsed = parseApiError(err)
      showError(parsed.message || 'An unexpected error occurred')
      setSubmissionPhase('review')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Build highlighted HTML from marker text:
  //   <<value>>     → yellow highlight  (AI-inferred — user reviews / corrects)
  //   [field]       → red highlight     (missing — user fills in)
  //   {{TOKEN}}     → blue highlight    (system-stamped per carrier at send time)
  const buildHighlightedHtml = (text) => {
    // Split on markers BEFORE HTML-escaping to avoid encoding issues
    const parts = text.split(/(<<[^>]+>>|\[[^\]]+\]|\{\{[^}]+\}\})/)
    return parts.map(part => {
      if (part.startsWith('<<') && part.endsWith('>>')) {
        const inner = part.slice(2, -2)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return `<mark class="hl-assumed">${inner}</mark>`
      }
      if (part.startsWith('[') && part.endsWith(']')) {
        const inner = part
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return `<mark class="hl-missing">${inner}</mark>`
      }
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const inner = part
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return `<mark class="hl-placeholder">${inner}</mark>`
      }
      return part
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    }).join('')
  }

  // Set contentEditable HTML only when entering review mode (not on every render)
  useEffect(() => {
    if (submissionPhase === 'review' && editableRef.current) {
      editableRef.current.innerHTML = buildHighlightedHtml(rfqText)
    }
  }, [submissionPhase]) // only on phase change, not rfqText changes

  const handleResetForm = () => {
    setSubmissionPhase(null)
    setTrackingRequests([])
    setOriginalRfqText('')
    setRfqText('')
    setSelectedCarriers([])
    setCarrierSearch('')
    setValidationErrors([])
    setValidationAttempted(false)
    setSkipValidationChecked(false)
  }

  const handleRetryWithEdits = () => {
    // Restore original RFQ text so user can edit and resubmit
    if (originalRfqText) {
      setRfqText(originalRfqText)
    }
    setSubmissionPhase(null)
    setTrackingRequests([])
    setOriginalRfqText('')
    setValidationErrors([])
    setValidationAttempted(false)
    setSkipValidationChecked(false)
  }

  const handleUseExample = () => {
    setRfqText(exampleRFQ)
  }

  const getStatusClass = (status) => {
    switch(status) {
      case 'Pending': return 'status-pending'
      case 'Quoted': return 'status-quoted'
      case 'Confirmed': return 'status-confirmed'
      default: return ''
    }
  }

  return (
    <div className="home">
      {/* Briefing strip — full-bleed paper-coloured header. Greeting on the
          left, live pulse pill in the middle, single "New RFQ" anchor on
          the right. The thin three-segment top rail (alert / ready /
          waiting) doubles as a legend for status colours used downstream. */}
      <section className="briefing">
        <div className="briefing-rail" aria-hidden="true" />
        <div className="briefing-inner">
          <div className="briefing-left">
            <p className="briefing-date">{briefingDate}</p>
            <h1 className="briefing-greeting">
              {greeting}
              {userShort && (
                <>
                  , <span className="briefing-name">{userShort}</span>
                </>
              )}
              <span className="briefing-period">.</span>
            </h1>
          </div>
          <div className="briefing-pulse" role="status" aria-live="polite">
            <span className="pulse-dot" aria-hidden="true" />
            <span className="pulse-counts">
              <strong>{requestsLoading ? '–' : awaitingCount}</strong> awaiting
              <span className="pulse-sep">·</span>
              <strong>{requestsLoading ? '–' : readyCount}</strong> quoted
              {urgentCount > 0 && (
                <>
                  <span className="pulse-sep">·</span>
                  <strong className="pulse-alert">{urgentCount}</strong> needs you
                </>
              )}
            </span>
          </div>
        </div>
      </section>

      <main className="page">
        {urgentCount > 0 && (
          <button
            type="button"
            className="attention-banner"
            onClick={() => navigate('/rfqs')}
          >
            <span className="attention-dot" aria-hidden="true" />
            <span className="attention-text">
              {urgentCount} request{urgentCount > 1 ? 's' : ''} need your attention
            </span>
            <span className="attention-arrow" aria-hidden="true">→</span>
          </button>
        )}

        <section id="rfq-composer" className="rfq-form-section">
          <header className="composer-head">
            <h2>New quote request</h2>
          </header>

          <form onSubmit={handleSubmit} className="rfq-form">
            {/* Carrier Selection */}
            <div className="carrier-selection-section">
              <label htmlFor="carrierSearch" className="carrier-label">
                Carrier
              </label>
              <div className="carrier-dropdown" ref={dropdownRef}>
                <div className="carrier-input-wrapper">
                  <input
                    type="text"
                    id="carrierSearch"
                    className="carrier-search-input"
                    placeholder="Search carriers or type to filter..."
                    value={carrierSearch}
                    onChange={(e) => {
                      setCarrierSearch(e.target.value)
                      setIsCarrierDropdownOpen(true)
                    }}
                    onFocus={() => setIsCarrierDropdownOpen(true)}
                  />
                  <span className="dropdown-icon">▼</span>
                </div>

                {isCarrierDropdownOpen && (
                  <div className="carrier-dropdown-menu">
                    {carriersLoading ? (
                      <div className="carrier-option loading">
                        Loading carriers...
                      </div>
                    ) : carriersError ? (
                      <div className="carrier-option error" onClick={refetchCarriers}>
                        Failed to load carriers. Click to retry.
                      </div>
                    ) : filteredCarriers.length > 0 ? (
                      <>
                        {filteredCarriers.map(carrier => {
                          const isSelected = selectedCarriers.some(p => p.id === carrier.id)
                          return (
                            <div
                              key={carrier.id}
                              className={`carrier-option ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleCarrierToggle(carrier)}
                            >
                              <div className="carrier-option-content">
                                <span className="carrier-name">{carrier.name}</span>
                                <span className="carrier-type">{carrier.type}</span>
                              </div>
                              <div className="carrier-option-meta">
                                <span className="carrier-email">{carrier.contactInfo}</span>
                                {isSelected && <span className="check-icon">✓</span>}
                              </div>
                            </div>
                          )
                        })}
                      </>
                    ) : (
                      <div className="carrier-option no-results">
                        No carriers found
                      </div>
                    )}
                    <div
                      className="carrier-option add-new-option"
                      onClick={() => {
                        setShowAddCarrierModal(true)
                        setIsCarrierDropdownOpen(false)
                      }}
                    >
                      <span className="add-icon">+</span>
                      Add New Carrier to Registry
                    </div>
                  </div>
                )}
              </div>

              {selectedCarriers.length > 0 && (
                <div className="selected-carriers-container">
                  <label className="selected-label">Selected Carriers ({selectedCarriers.length}):</label>
                  <div className="selected-carriers-list">
                    {selectedCarriers.map(carrier => (
                      <div key={carrier.id} className="selected-carrier-chip">
                        <div className="chip-content">
                          <span className="chip-name">{carrier.name}</span>
                          <span className="chip-type">{carrier.type}</span>
                        </div>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveCarrier(carrier.id)}
                          title="Remove carrier"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="ai-input-section">
              <div className="input-header">
                <div className="label-with-badge">
                  <label htmlFor="rfqText" className="input-label">
                    RFQ Details
                  </label>
                </div>
              </div>

              <div className="ai-textarea-wrapper">
                {submissionPhase === 'review' ? (
                  <div
                    ref={editableRef}
                    className="ai-textarea ai-textarea--editable"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={() => {
                      if (editableRef.current) {
                        setRfqText(editableRef.current.innerText)
                      }
                    }}
                  />
                ) : (
                  <>
                    <textarea
                      id="rfqText"
                      value={rfqText}
                      onChange={(e) => setRfqText(e.target.value)}
                      required
                      className="ai-textarea"
                    />
                    {!rfqText && (
                      <div className={`typing-placeholder${typingFading ? ' typing-fade-out' : ''}`}>
                        {typingText}
                        {!typingFading && <span className="typing-cursor">|</span>}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="char-count">
                {rfqText.length} characters
              </div>

              {/* AI review info / errors shown under textarea */}
              {(submissionPhase === 'review' || submissionPhase === 'error') && (() => {
                const extracted = trackingRequests.find(t => t.status === 'extracted' && t.extraction)
                const failed = trackingRequests.find(t => t.status === 'error')
                const ext = extracted?.extraction
                const originCity = ext?.origin?.city
                const destCity = ext?.destination?.city
                return (
                  <div className="review-under-textarea">
                    {/* Validation errors (inline, not toast) */}
                    {validationErrors.length > 0 && (
                      <div className="review-error-msg">
                        {validationErrors.map((err, i) => (
                          <div key={i}>{err}</div>
                        ))}
                      </div>
                    )}

                    {/* Error/failure message */}
                    {failed && (
                      <div className="review-error-msg">
                        {failed.error}
                      </div>
                    )}

                    {/* AI summary for extracted requests */}
                    {ext && (
                      <>
                        {ext.request_shipment_type && (
                          <div className="ai-summary">
                            {ext.request_shipment_type}
                            {originCity && destCity && ` — ${originCity} → ${destCity}`}
                          </div>
                        )}
                        {ext.confidence_score != null && (
                          <div className="review-confidence">
                            AI Confidence: <strong>{(ext.confidence_score * 100).toFixed(0)}%</strong>
                          </div>
                        )}
                      </>
                    )}

                    <div className="review-legend">
                      <span className="legend-item"><span className="legend-assumed">highlighted</span> AI-inferred</span>
                      <span className="legend-item"><span className="legend-missing">[field]</span> Needs your input</span>
                      <span className="legend-item"><span className="legend-placeholder">{'{{token}}'}</span> Filled per carrier at send time</span>
                    </div>

                    {/* Skip-validation checkbox — visible in review mode
                        (after the AI extraction round has run) so the user
                        can opt out of the validation step before clicking
                        Confirm & Send. */}
                    {submissionPhase === 'review' && (
                      <label className="skip-validation-check">
                        <input
                          type="checkbox"
                          checked={skipValidationChecked}
                          onChange={(e) => setSkipValidationChecked(e.target.checked)}
                        />
                        <span>Skip further validation</span>
                      </label>
                    )}
                  </div>
                )
              })()}
            </div>

            <div className="form-actions">
              {submissionPhase === 'review' ? (
                <>
                  <button type="button" className="btn-primary" onClick={handleConfirmSend}>
                    Confirm & Send to Carrier
                  </button>
                  <button type="button" className="btn-secondary" onClick={handleResetForm}>
                    Cancel
                  </button>
                </>
              ) : ['validating', 'sending', 'submitting', 'processing', 'success'].includes(submissionPhase) ? (
                null
              ) : submissionPhase === 'error' ? (
                <>
                  <button type="button" className="btn-primary" onClick={handleRetryWithEdits}>
                    Edit & Retry
                  </button>
                  <button type="button" className="btn-secondary" onClick={handleResetForm}>
                    Start Over
                  </button>
                </>
              ) : (
                <>
                  <button type="submit" className="btn-primary" disabled={!rfqText.trim() || selectedCarriers.length === 0 || !!submissionPhase}>
                    Submit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setRfqText('')
                      setSelectedCarriers([])
                      setCarrierSearch('')
                    }}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </form>

        </section>

        <section className="recent">
          <header className="recent-head">
            <h2>Recent</h2>
            <button
              type="button"
              className="recent-link"
              onClick={() => navigate('/rfqs')}
            >
              View all →
            </button>
          </header>
          {requestsLoading ? (
            <p className="recent-empty">Loading…</p>
          ) : recentQuotes.length === 0 ? (
            <p className="recent-empty">No requests yet.</p>
          ) : (
            <ul className="recent-table">
              {recentQuotes.map(quote => (
                <li
                  key={quote.id}
                  className="recent-row"
                  onClick={() => navigate('/rfqs')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/rfqs')
                    }
                  }}
                >
                  <code className="recent-id">{quote.id?.slice(0, 8)}</code>
                  <span className="recent-route">
                    {quote.origin} → {quote.destination}
                  </span>
                  <span className={`recent-status ${getStatusClass(quote.status)}`}>
                    {quote.status}
                  </span>
                  <span className="recent-date">{quote.date}</span>
                  {quote.isUnseen && <span className="badge-new">New</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {/* Add Carrier Modal */}
      {showAddCarrierModal && (
        <div className="modal-overlay" onClick={() => !isSubmitting && setShowAddCarrierModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Carrier</h3>
              <button className="modal-close" onClick={() => setShowAddCarrierModal(false)} disabled={isSubmitting}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-form-group">
                <label>Carrier Name *</label>
                <input
                  type="text"
                  value={newCarrier.name}
                  onChange={(e) => setNewCarrier({...newCarrier, name: e.target.value})}
                  placeholder="e.g., ABC Logistics"
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-form-group">
                <label>Freight Type *</label>
                <select
                  value={newCarrier.type}
                  onChange={(e) => setNewCarrier({...newCarrier, type: e.target.value})}
                  disabled={isSubmitting}
                >
                  <option value="">Select freight type...</option>
                  <option value="Air">Air</option>
                  <option value="Rail">Rail</option>
                  <option value="Road">Road</option>
                  <option value="Sea">Sea</option>
                </select>
              </div>
              <div className="modal-form-group">
                <label>Contact Types * (Select one or more)</label>
                <div className="contact-type-options">
                  <label className="contact-type-checkbox">
                    <input
                      type="checkbox"
                      checked={newCarrier.contactTypes.includes('email')}
                      onChange={() => handleToggleContactType('email')}
                      disabled={isSubmitting}
                    />
                    <span>📧 Email</span>
                  </label>
                  <div className="contact-type-disabled-group">
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>📞 Phone</span>
                    </label>
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>🌐 Website</span>
                    </label>
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>🔗 API</span>
                    </label>
                    <span className="coming-soon-badge">Coming soon</span>
                  </div>
                </div>
              </div>

              {newCarrier.contactTypes.length > 1 && (
                <div className="modal-form-group">
                  <label>Primary Contact Type *</label>
                  <select
                    value={newCarrier.primaryContactType}
                    onChange={(e) => setNewCarrier({ ...newCarrier, primaryContactType: e.target.value })}
                    disabled={isSubmitting}
                  >
                    <option value="">Select primary contact type...</option>
                    {newCarrier.contactTypes.includes('email') && <option value="email">📧 Email</option>}
                    {newCarrier.contactTypes.includes('phone') && <option value="phone">📞 Phone</option>}
                    {newCarrier.contactTypes.includes('website') && <option value="website">🌐 Website</option>}
                    {newCarrier.contactTypes.includes('api') && <option value="api">🔗 API</option>}
                  </select>
                </div>
              )}

              {(newCarrier.contactTypes.includes('email') || newCarrier.contactTypes.includes('phone')) && (
                <div className="modal-form-group">
                  <label>Contact Name *</label>
                  <input
                    type="text"
                    value={newCarrier.contacts?.email?.name || newCarrier.contacts?.phone?.name || ''}
                    onChange={(e) => {
                      const val = e.target.value
                      setNewCarrier(prev => ({
                        ...prev,
                        contacts: {
                          ...prev.contacts,
                          email: { ...prev.contacts.email, name: val },
                          phone: { ...prev.contacts.phone, name: val },
                        }
                      }))
                    }}
                    placeholder="John Doe"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('email') && (
                <div className="modal-form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={newCarrier.contacts.email.address}
                    onChange={(e) => handleContactChange('email', 'address', e.target.value)}
                    placeholder="quotes@carrier.com"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('phone') && (
                <div className="modal-form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={newCarrier.contacts.phone.number}
                    onChange={(e) => handleContactChange('phone', 'number', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('website') && (
                <>
                  <div className="modal-form-group">
                    <label>Website URL *</label>
                    <input
                      type="url"
                      value={newCarrier.contacts.website.url}
                      onChange={(e) => handleContactChange('website', 'url', e.target.value)}
                      placeholder="https://www.carrier.com/contact"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>Login Username</label>
                    <input
                      type="text"
                      value={newCarrier.contacts.website.username}
                      onChange={(e) => handleContactChange('website', 'username', e.target.value)}
                      placeholder="username (optional)"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>Login Password</label>
                    <input
                      type="password"
                      value={newCarrier.contacts.website.password}
                      onChange={(e) => handleContactChange('website', 'password', e.target.value)}
                      placeholder="(optional)"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}

              {newCarrier.contactTypes.includes('api') && (
                <>
                  <div className="modal-form-group">
                    <label>API URL *</label>
                    <input
                      type="url"
                      value={newCarrier.contacts.api.url}
                      onChange={(e) => handleContactChange('api', 'url', e.target.value)}
                      placeholder="https://api.carrier.com/quotes"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>API Headers (JSON)</label>
                    <textarea
                      value={newCarrier.contacts.api.headers}
                      onChange={(e) => handleContactChange('api', 'headers', e.target.value)}
                      placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                      rows="3"
                      className="api-headers-textarea"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddCarrierModal(false)} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAddNewCarrier} disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Carrier'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal overlay — spinner during loading, checkmark on success */}
      {['submitting', 'processing', 'validating', 'sending', 'success'].includes(submissionPhase) && (
        <div className="spinner-overlay">
          <div className="spinner-modal">
            {submissionPhase === 'success' ? (
              <>
                <div className="modal-success-icon">&#10003;</div>
                <p className="spinner-modal-text">Request sent successfully</p>
              </>
            ) : (
              <>
                <div className="spinner spinner--lg"></div>
                <p className="spinner-modal-text">
                  {submissionPhase === 'submitting' && 'Submitting request...'}
                  {submissionPhase === 'processing' && 'AI is extracting shipment details...'}
                  {submissionPhase === 'validating' && 'Validating your request...'}
                  {submissionPhase === 'sending' && 'Sending to carrier...'}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
